import { Redirect } from "react-router-dom";
import { useState, useEffect, useCallback, useMemo } from "react";
import {
    Accordion,
    Button,
    Divider,
    Form,
    FormField,
    Icon,
    Message,
    Modal,
    Radio,
    Segment,
    TextArea,
} from "semantic-ui-react";
import { useHistory, useParams } from "react-router";
import Logo from "../../images/entsoe_logo_white.svg";
import { useSelector } from "react-redux";
import { AppState } from "../../store/configureStore";
import { UserData, UserRole } from "../../actions/authentificationActions";
import { toast } from "../..";
import "./ParameterView.css";
import { getParameterAnswer, updateAnswerValue } from "../../services/parameterAnswerService";
import { initParameterAnswer, IParameterAnswer } from "../../models/parameterAnswer";
import { getParameter } from "../../services/parametersService";
import { displayNetworkCodeOption, IParameter } from "../../models/parameter";
import { localeString, timeDateFormat } from "../../helpers/commonHelper";
import { getAnswerValues } from "../ImplementationMaps/ImplementationMapsHelper";
import debounce from "lodash.debounce";

export const ParameterView = () => {
    const user: UserData = useSelector((state: AppState) => state.user);
    const params: { id: string; answerId?: string } = useParams();
    const history = useHistory();
    const [answer, setAnswer] = useState<IParameterAnswer>();
    const [parameter, setParameter] = useState<IParameter>();
    const [errors, setErrors] = useState<string[]>([]);
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [validated, setValidated] = useState<boolean>(false);
    const [validationSuccess, setValidationSuccess] = useState<boolean>(false);
    const [loading, setLoading] = useState(true);
    const [remarkOpen, setRemarkOpen] = useState<boolean>(false);

    const disabled = useMemo(() => {
        return (
            user.role !== UserRole.NCLP ||
            answer?.isSubmitted ||
            (parameter && new Date(parameter?.deadline) < new Date())
        );
    }, [answer, user, parameter]);

    const fetchParameterAnswer = useCallback(async () => {
        setLoading(true);
        try {
            const parameter = await getParameter(params.id);
            setParameter(parameter);
        } catch {
            toast("Error fetching parameter", false, 3000);
        }
        let answer: IParameterAnswer;

        if (params.answerId !== "null" && params.answerId) {
            try {
                answer = await getParameterAnswer(params.answerId);
                setRemarkOpen(answer.remark !== "");
            } catch {
                toast("Error fetching answer", false, 3000);
                answer = initParameterAnswer;
            }
        } else {
            answer = {
                guid: "",
                answerValue: 0,
                remark: "",
                timeAnswered: "",
                parameterGuid: params.id,
                countryGuid: user.countryGuid ?? "",
                countryName: user.countryName ?? "",
                isSubmitted: false,
            };
        }
        setAnswer(answer);

        setLoading(false);
    }, [params.id, params.answerId, user]);

    useEffect(() => {
        fetchParameterAnswer();
    }, [fetchParameterAnswer]);

    const validate = () => {
        let errors: string[] = [];
        if (answer?.answerValue === 0) {
            errors.push("Answer not selected");
        }
        setErrors(errors);
        return errors.length === 0;
    };

    const answerValues: string[] = useMemo(() => {
        if (!parameter) {
            return [];
        }
        return getAnswerValues(parameter);
    }, [parameter]);

    const openSubmitModal = () => {
        setModalOpen(true);
        setValidated(false);
        setValidationSuccess(validate());
        setValidated(true);
    };

    const onSubmit = async () => {
        if (!answer) {
            return;
        }
        try {
            setLoading(true);
            const res = await updateAnswerValue({ ...answer, isSubmitted: true });
            if (res) {
                setAnswer(res);
                toast("Answer submitted", true, 3000);
            }
        } catch {
            toast("Error submitting answer", false, 3000);
        }
        setLoading(false);
        setModalOpen(false);
        history.goBack();
    };

    const handleUpdateAnswer = async (value: number) => {
        if (!answer) {
            return;
        }
        try {
            setLoading(true);
            const res = await updateAnswerValue({ ...answer, answerValue: value });
            if (res) {
                setAnswer(res);
                toast("Answer saved", true, 1000);
            }
        } catch {
            toast("Error saving answer", false, 3000);
        }

        setLoading(false);
    };

    const handleUpdateRemark = async (remark: string) => {
        if (!answer) {
            return;
        }
        try {
            setLoading(true);
            const res = await updateAnswerValue({ ...answer, remark });
            if (res) {
                toast("Remark saved", true, 1000);
            }
        } catch {
            toast("Error saving remark", false, 3000);
        }
        setLoading(false);
    };

    const debouncedUpdateRemark = useMemo(
        () =>
            debounce((remark: string) => {
                handleUpdateRemark(remark);
            }, 1000),
        []
    );

    const valdationStatusText = useMemo(() => {
        if (validated) {
            return validationSuccess ? "Confirm submit" : "Validation failed!";
        }
        return "Validating...";
    }, [validated, validationSuccess]);

    if (user.role === UserRole.Viewer) {
        return <Redirect to="/unauthorized" />;
    }

    return (
        <div className="form-view-container">
            <div className="form-header">
                <img src={Logo} alt="Logo" />
            </div>

            <div className="form-view">
                <Form>
                    {answerValues.map((answerValue, index) => (
                        <FormField key={index} className="parameter-answer-field">
                            <div className={`color-rectangle answer${index + 1}`}></div>
                            <Radio
                                label={answerValue}
                                name="radioGroup"
                                value={index + 1}
                                checked={answer?.answerValue === index + 1}
                                onChange={() => handleUpdateAnswer(index + 1)}
                                disabled={disabled}
                            />
                        </FormField>
                    ))}
                    <Accordion className="remark-content">
                        <Accordion.Title
                            active={remarkOpen}
                            onClick={setRemarkOpen.bind(null, !remarkOpen)}
                        >
                            <Icon name="dropdown" />
                            <span>Remark</span>
                        </Accordion.Title>
                        <Accordion.Content active={remarkOpen}>
                            <TextArea
                                name={"remark-text"}
                                value={answer?.remark}
                                disabled={disabled}
                                onChange={(_e, data) => {
                                    setAnswer(prev => {
                                        if (!prev) {
                                            return prev;
                                        }
                                        return { ...prev, remark: data.value as string };
                                    });
                                    debouncedUpdateRemark(data.value as string);
                                }}
                                onBlur={() => handleUpdateRemark(answer?.remark as string)}
                            />
                        </Accordion.Content>
                    </Accordion>
                </Form>

                <div className="buttons-container">
                    <Button onClick={history.goBack}>Back</Button>
                    {user.role === UserRole.NCLP && (
                        <Button primary onClick={openSubmitModal} disabled={disabled}>
                            Submit
                        </Button>
                    )}
                </div>
            </div>

            <Segment id="form-info-container" className="form-info">
                <div>
                    <b>Country: </b>
                    {user.countryName}
                </div>
                <Divider />
                <div>
                    <b>Year: </b>
                    {parameter?.year}
                </div>
            </Segment>

            <nav id="section-navigation">
                <Segment id="parameter-info-right" className="form-info">
                    <div>
                        <b>Deadline: </b>
                        {parameter?.deadline &&
                            new Date(parameter.deadline).toLocaleString(
                                localeString,
                                timeDateFormat
                            )}
                    </div>
                    <Divider />
                    <div>
                        <b>Network code: </b>
                        {displayNetworkCodeOption(parameter?.networkCode)}
                    </div>
                </Segment>
            </nav>

            <Modal onClose={() => setModalOpen(false)} open={modalOpen}>
                <Modal.Header>{valdationStatusText}</Modal.Header>
                <Modal.Content>
                    {validated && validationSuccess && (
                        <>
                            <Message icon color="green">
                                <Icon name="check circle outline" color="green" />
                                Validation successfull!
                            </Message>
                            <p>
                                Are you sure you want to submit this answer? Submited values can no
                                longer be changed!
                            </p>
                        </>
                    )}
                    {validated && !validationSuccess && (
                        <>
                            <Message icon color="red">
                                <Icon name="times circle outline" color="red" />
                                Validation failed!
                            </Message>
                            {errors.map((error, index) => (
                                <p key={index}>
                                    <Icon name="warning sign" />
                                    {error}
                                </p>
                            ))}
                            <p>Fix errors before sumbiting!</p>
                        </>
                    )}
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={() => setModalOpen(false)} disabled={loading}>
                        Close
                    </Button>
                    <Button
                        primary
                        loading={loading}
                        disabled={!validated || !validationSuccess}
                        onClick={onSubmit}
                    >
                        Submit
                    </Button>
                </Modal.Actions>
            </Modal>
        </div>
    );
};
